import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Mail, MapPin, Clock, Facebook } from "lucide-react";
import { Logo } from "./Logo";
import { ChatWidget } from "./ChatWidget";
import { BUSINESS, NAV_LINKS } from "../lib/data";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <Logo imgClassName="h-14 w-auto md:h-20 lg:h-24" />

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-primary/10 text-primary" : "text-slate-600 hover:text-primary"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-button"
            className="group flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_4px_14px_rgb(42,157,143,0.35)] transition-all duration-300 hover:shadow-[0_6px_20px_rgb(42,157,143,0.5)] hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            Call Now
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="rounded-full p-2 text-slate-700 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-200/60 bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium ${
                      isActive ? "bg-primary/10 text-primary" : "text-slate-700"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-call-button"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 font-semibold text-primary-foreground"
              >
                <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-4 md:px-10">
      <div className="md:col-span-2">
        <Logo variant="full" />
        <p className="mt-4 max-w-sm text-base leading-relaxed text-slate-600">
          Warm, dependable residential and small-office cleaning for {BUSINESS.region}. Book your
          free home visit before any pricing.
        </p>
        <a
          href={BUSINESS.facebook}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-facebook"
          aria-label="Wisp Home Cleaning on Facebook"
          className="mt-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:text-white"
        >
          <Facebook className="h-5 w-5" />
        </a>
      </div>
      <div>
        <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-slate-900">Contact</h4>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          <li>
            <a href={BUSINESS.phoneHref} data-testid="footer-phone" className="flex items-center gap-2 hover:text-primary">
              <Phone className="h-4 w-4 text-primary" /> {BUSINESS.phone}
            </a>
          </li>
          <li>
            <a href={BUSINESS.emailHref} data-testid="footer-email" className="flex items-center gap-2 hover:text-primary">
              <Mail className="h-4 w-4 text-primary" /> {BUSINESS.email}
            </a>
          </li>
          <li className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 text-primary" /> {BUSINESS.region}
          </li>
        </ul>
      </div>
      <div>
        <h4 className="font-heading text-sm font-semibold uppercase tracking-widest text-slate-900">Hours</h4>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          {BUSINESS.hours.map((h) => (
            <li key={h.day} className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 text-primary" />
              <span>
                <span className="block font-medium text-slate-800">{h.day}</span>
                {h.time}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-200 py-4 text-center text-xs font-medium uppercase tracking-wider text-slate-500">
      Multiple Award-Winning · Serving Hamilton Since 2019 · Registered, Insured &amp; Bondable · Police Checks Available
    </div>
    <div className="border-t border-slate-200 py-6 text-center text-sm text-slate-500">
      © {new Date().getFullYear()} {BUSINESS.name}. Serving Hamilton & surrounding communities.
    </div>
  </footer>
);

export const Layout = () => (
  <div className="flex min-h-screen flex-col">
    <Navbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
    {/* Mobile sticky click-to-call bar */}
    <a
      href={BUSINESS.phoneHref}
      data-testid="sticky-mobile-call"
      className="fixed inset-x-4 bottom-4 z-40 flex items-center justify-center gap-2 rounded-full bg-primary py-3.5 font-semibold text-primary-foreground shadow-[0_8px_30px_rgb(42,157,143,0.45)] lg:hidden"
    >
      <Phone className="h-5 w-5" /> Call Now — {BUSINESS.phone}
    </a>
    <ChatWidget />
  </div>
);
